import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Trophy, Medal, Music, Infinity } from 'lucide-react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { toast } from 'sonner';

// Import tab components from ContentLibrary folder
import { 
  SchedulesTab, 
  DisciplinesTab, 
  GradesTab, 
  DancesTab, 
  FiguresTab 
} from './ContentLibrary';

export default function ContentLibraryPage() {
  const [activeTab, setActiveTab] = useState('schedules');
  const [schedules, setSchedules] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [dances, setDances] = useState([]);
  const [figures, setFigures] = useState([]);
  const [searchDance, setSearchDance] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenAddSchedule, setAutoOpenAddSchedule] = useState(false);

  // Dashboard Quick Action → `?action=create-schedule` lands here.
  // Switch to the Schedules tab and pop the Add Schedule dialog open.
  useEffect(() => {
    if (searchParams.get('action') === 'create-schedule') {
      setActiveTab('schedules');
      setAutoOpenAddSchedule(true);
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Fetch current user
  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };
  
  // Fetch functions
  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/content/schedules`, { withCredentials: true });
      setSchedules(res.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };
  
  const fetchDisciplines = async () => {
    try {
      const res = await axios.get(`${API}/content/disciplines`, { withCredentials: true });
      setDisciplines(res.data);
    } catch (error) {
      console.error('Error fetching disciplines:', error);
    }
  };
  
  const fetchDances = async () => {
    try {
      const res = await axios.get(`${API}/content/compulsory-dances`, { withCredentials: true });
      setDances(res.data);
    } catch (error) {
      console.error('Error fetching dances:', error);
    }
  };
  
  const fetchFigures = async () => {
    try {
      const res = await axios.get(`${API}/content/figures`, { withCredentials: true });
      setFigures(res.data);
    } catch (error) {
      console.error('Error fetching figures:', error);
    }
  };
  
  useEffect(() => {
    fetchCurrentUser();
    fetchSchedules();
    fetchDisciplines();
    fetchDances();
    fetchFigures();
  }, []);
  
  // Check if user can reorder (super users only)
  const canReorder = currentUser?.role === 'super';
  
  // Drag and drop handler for schedules and disciplines
  const handleDragEnd = async (event, type) => {
    // Only super users can reorder
    if (!canReorder) {
      toast.error('Only Super Users can reorder items');
      return;
    }
    
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    if (type === 'schedules') {
      const oldIndex = schedules.findIndex(s => s.schedule_id === active.id);
      const newIndex = schedules.findIndex(s => s.schedule_id === over.id);
      const newOrder = arrayMove(schedules, oldIndex, newIndex);
      setSchedules(newOrder);
      
      try {
        await axios.post(`${API}/content/schedules/reorder`, 
          newOrder.map(s => s.schedule_id), 
          { withCredentials: true }
        );
        toast.success('Order updated');
      } catch (error) {
        toast.error('Failed to update order');
        fetchSchedules();
      }
    } else if (type === 'disciplines') {
      const oldIndex = disciplines.findIndex(d => d.discipline_id === active.id);
      const newIndex = disciplines.findIndex(d => d.discipline_id === over.id);
      const newOrder = arrayMove(disciplines, oldIndex, newIndex);
      setDisciplines(newOrder);
      
      try {
        await axios.post(`${API}/content/disciplines/reorder`, 
          newOrder.map(d => d.discipline_id), 
          { withCredentials: true }
        );
        toast.success('Order updated');
      } catch (error) {
        toast.error('Failed to update order');
        fetchDisciplines();
      }
    }
  };
  
  // Filter dances by search term
  const filteredDances = dances.filter(d => 
    d.name.toLowerCase().includes(searchDance.toLowerCase()) ||
    d.tempo.toLowerCase().includes(searchDance.toLowerCase())
  );
  
  // Tab configuration
  const tabs = [
    { id: 'schedules', label: 'Schedules', icon: Clock },
    { id: 'disciplines', label: 'Disciplines', icon: Trophy },
    { id: 'grades', label: 'Grades', icon: Medal },
    { id: 'dances', label: 'Compulsory Dances', icon: Music },
    { id: 'figures', label: 'Figures', icon: Infinity },
  ];
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2D4A3E' }}>Content Library</h1>
        <p className="text-muted-foreground mt-1">Manage skating-specific data like schedules, disciplines, and grades</p>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-stone-200">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive 
                    ? 'border-green-700 text-green-800 font-medium' 
                    : 'border-transparent text-muted-foreground hover:text-green-700 hover:border-green-300'
                }`}
                style={isActive ? { color: '#2D4A3E' } : {}}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Tab Content */}
      <div>
        {activeTab === 'schedules' && (
          <SchedulesTab 
            schedules={schedules} 
            setSchedules={setSchedules} 
            fetchSchedules={fetchSchedules} 
            handleDragEnd={handleDragEnd}
            sensors={sensors}
            currentUser={currentUser}
            autoOpenAdd={autoOpenAddSchedule}
            onAutoOpenAddConsumed={() => setAutoOpenAddSchedule(false)}
          />
        )}
        
        {activeTab === 'disciplines' && (
          <DisciplinesTab 
            disciplines={disciplines} 
            setDisciplines={setDisciplines} 
            fetchDisciplines={fetchDisciplines} 
            handleDragEnd={handleDragEnd}
            sensors={sensors}
            currentUser={currentUser}
          />
        )}
        
        {activeTab === 'grades' && (
          <GradesTab 
            sensors={sensors} 
            currentUser={currentUser}
            schedules={schedules}
          />
        )}
        
        {activeTab === 'dances' && (
          <DancesTab 
            dances={filteredDances} 
            fetchDances={fetchDances} 
            searchDance={searchDance} 
            setSearchDance={setSearchDance}
            currentUser={currentUser}
          />
        )}
        
        {activeTab === 'figures' && (
          <FiguresTab 
            figures={figures} 
            fetchFigures={fetchFigures}
            currentUser={currentUser}
          />
        )}
      </div>
    </div>
  );
}
